import React, { useEffect, useState } from "react";
import { Button, Tabs, Table, Space, Tag, Popconfirm, message } from "antd";
import { useNavigate, useParams, Link } from "react-router-dom";
import moment from "moment";
import useRequest from "../../hooks/useRequest";
import apiPath from "../../constants/apiPath";
import PageTransition from "../../components/ui/PageTransition";
import EmptyState from "../../components/ui/EmptyState";
import StatusTag from "../../components/ui/StatusTag";
import EmployeeCalendarTab from "../Attendance/EmployeeCalendarTab";
import axios from "axios";
import { PaperClipOutlined } from "@ant-design/icons";
import { regionName } from "../../helper/regions";
import { Avatar, Field, Section } from "./employeeUi";
import { SECTIONS, COLUMNS } from "./employeeSchema";
import { FieldValue } from "./EmployeeFields";

const fmtDays = (n) => (Number.isInteger(n) ? String(n) : Number(n).toFixed(1));

const BalanceTile = ({ label, color, kpi }) => (
  <div style={{ flex: 1, borderLeft: `4px solid ${color}`, padding: "2px 0 2px 12px" }}>
    <div style={{ color: "#6B7280", fontSize: 12 }}>{label}</div>
    <div style={{ fontSize: 24, fontWeight: 700 }}>
      {fmtDays(kpi.remaining)} <span style={{ fontSize: 12, color: "#6B7280", fontWeight: 400 }}>left of {fmtDays(kpi.entitled)}</span>
    </div>
    <div style={{ color: "#6B7280", fontSize: 12 }}>
      {fmtDays(kpi.taken)} taken{kpi.pending ? ` · ${fmtDays(kpi.pending)} pending` : ""}
    </div>
  </div>
);

const EmployeeProfile = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { request } = useRequest();
  const [employee, setEmployee] = useState(null);
  const [balance, setBalance] = useState(null); // yearly annual / sick leave
  const [leaves, setLeaves] = useState([]);
  const [missing, setMissing] = useState(false);

  const load = () => {
    request({
      url: `${apiPath.employees}/${id}`,
      method: "GET",
      onSuccess: (res) => setEmployee(res?.data || null),
      onError: () => setMissing(true),
    });
    const now = moment();
    request({
      url: `${apiPath.employeeMonth(id)}?year=${now.year()}&month=${now.month() + 1}`,
      method: "GET",
      onSuccess: (res) => setBalance(res?.data?.leaveBalance || null),
    });
    request({ url: `${apiPath.leaveRequests}?employee=${id}`, method: "GET", onSuccess: (res) => setLeaves(res?.data?.data || []) });
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const downloadFile = async (a) => {
    try {
      const token = localStorage.getItem("i_ems_admin_token") || "";
      const r = await axios.get(`${apiPath.baseURL}${apiPath.employees}/${id}/attachments/${a._id}/download`, { headers: { Authorization: `Bearer ${token}` }, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", a.name);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      message.error("Could not download the file");
    }
  };

  const toggleActive = () =>
    request({
      url: `${apiPath.employees}/${id}/toggle-active`,
      method: "PATCH",
      onSuccess: () => {
        message.success("Status updated");
        load();
      },
    });

  const sendReset = () =>
    request({
      url: `${apiPath.employees}/${id}/send-reset-link`,
      method: "POST",
      onSuccess: () => message.success("Password reset link sent"),
      onError: (err) => message.error(err?.response?.data?.message || "Could not send the link"),
    });

  if (missing) {
    return (
      <PageTransition>
        <EmptyState message="This employee could not be found" actionLabel="Back to employees" onAction={() => navigate("/employees")} />
      </PageTransition>
    );
  }
  if (!employee) return <PageTransition>{null}</PageTransition>;

  const active = employee.employmentStatus === "active";
  const office = employee.assignedOffice;

  const grid = { display: "grid", gridTemplateColumns: "1fr", gap: "14px" };
  const viewSpecial = {
    account: (
      <div style={grid}>
        <Field label="Work email">{employee.email}</Field>
        <Field label="Status">{active ? "Active" : "Inactive"}</Field>
        <Field label="Sign-in">Email and password</Field>
      </div>
    ),
    assets:
      (employee.allocatedAssets || []).length ? (
        <Space wrap>
          {employee.allocatedAssets.map((x) => (
            <Tag key={x} color="geekblue">
              {x}
            </Tag>
          ))}
        </Space>
      ) : (
        <span style={{ color: "#9CA3AF" }}>None allocated</span>
      ),
    nextOfKin: (employee.nextOfKin || []).length ? (
      <div style={grid}>
        {employee.nextOfKin.map((k, i) => (
          <div key={i}>
            <div style={{ fontWeight: 600 }}>{k.name || "—"}</div>
            <div style={{ color: "#6B7280", fontSize: 12.5 }}>{[k.relationship, k.phone].filter(Boolean).join(" · ")}</div>
          </div>
        ))}
      </div>
    ) : (
      <span style={{ color: "#9CA3AF" }}>None added</span>
    ),
    customFields: (employee.customFields || []).length ? (
      <div style={grid}>
        {employee.customFields.map((c, i) => (
          <Field key={i} label={c.label || "Field"}>
            {c.value}
          </Field>
        ))}
      </div>
    ) : (
      <span style={{ color: "#9CA3AF" }}>None added</span>
    ),
    attachments: (employee.attachments || []).length ? (
      <div style={grid}>
        {employee.attachments.map((x) => (
          <Button key={x._id} type="link" style={{ padding: 0, height: "auto", justifyContent: "flex-start" }} icon={<PaperClipOutlined />} onClick={() => downloadFile(x)}>
            {x.name}
          </Button>
        ))}
      </div>
    ) : (
      <span style={{ color: "#9CA3AF" }}>No files</span>
    ),
  };

  const renderSection = (key) => {
    const section = SECTIONS[key];
    return (
      <Section key={key} title={section.title} hint={section.note}>
        {section.special ? (
          viewSpecial[key]
        ) : (
          <div style={grid}>
            {section.fields
              .map((f) => (
                <FieldValue key={f.path.join(".")} field={f} employee={employee} />
              ))}
          </div>
        )}
      </Section>
    );
  };

  const overview = (
    <div className="emp-cols">
      {COLUMNS.map((col, i) => (
        <div className="emp-col" key={i}>
          {i === 2 ? (
            <Section title="Leave balance" hint={balance ? String(balance.year) : ""}>
              {balance ? (
                <div style={{ display: "flex", gap: 12 }}>
                  <BalanceTile label="Annual leave" color="#8B5CF6" kpi={balance.annual} />
                  <BalanceTile label="Sick leave" color="#F59E0B" kpi={balance.sick} />
                </div>
              ) : (
                <span style={{ color: "#9CA3AF" }}>Not available</span>
              )}
            </Section>
          ) : null}
          {col.map(renderSection)}
        </div>
      ))}
    </div>
  );

  const leaveColumns = [
    { title: "Type", dataIndex: ["leaveType", "name"] },
    { title: "From", dataIndex: "startDate" },
    { title: "To", dataIndex: "endDate" },
    { title: "Days", dataIndex: "days" },
    { title: "Reason", dataIndex: "reason", ellipsis: true },
    { title: "Status", dataIndex: "status", render: (s) => <StatusTag status={s} /> },
  ];

  const leaveTab = (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      {balance ? (
        <Section title="Yearly balance" hint={String(balance.year)}>
          <div style={{ display: "flex", gap: 24, maxWidth: 640 }}>
            <BalanceTile label="Annual leave" color="#8B5CF6" kpi={balance.annual} />
            <BalanceTile label="Sick leave" color="#F59E0B" kpi={balance.sick} />
          </div>
        </Section>
      ) : null}
      <Section title="Leave requests" hint={<Link to="/leave?tab=requests">Open all leave requests</Link>}>
        <Table rowKey="_id" size="small" columns={leaveColumns} dataSource={leaves} pagination={{ pageSize: 8 }} locale={{ emptyText: <EmptyState message="No leave requests yet" /> }} />
      </Section>
    </div>
  );

  return (
    <PageTransition>
      <div style={{ color: "#6B7280", fontSize: 13, marginBottom: 10 }}>
        <Link to="/employees">Employees</Link> › {employee.name}
      </div>
      <div style={{ background: "#fff", border: "1px solid #E5E7EB", borderRadius: 14, padding: "20px 24px", display: "flex", alignItems: "center", gap: 20, flexWrap: "wrap" }}>
        <Avatar name={employee.name} size={72} />
        <div style={{ flex: 1, minWidth: 240 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
            <h1 style={{ fontSize: 22, margin: 0 }}>{employee.name}</h1>
            <StatusTag status={employee.employmentStatus} />
          </div>
          <div style={{ color: "#6B7280", marginTop: 5 }}>{[employee.designation, employee.department?.name, office?.name].filter(Boolean).join(" · ") || "No job details yet"}</div>
          <div style={{ color: "#6B7280", marginTop: 3, fontSize: 12.5 }}>
            {employee.employeeId}
            {employee.joiningDate ? ` · Joined ${moment(employee.joiningDate).format("DD MMM YYYY")}` : ""}
            {employee.region ? ` · ${regionName(employee.region)}` : ""}
          </div>
        </div>
        <Space wrap>
          <Popconfirm title={active ? "Deactivate this employee? They will no longer be able to sign in." : "Activate this employee?"} onConfirm={toggleActive}>
            <Button danger={active}>{active ? "Deactivate" : "Activate"}</Button>
          </Popconfirm>
          <Popconfirm title="Email this employee a link to set a new password?" onConfirm={sendReset}>
            <Button>Reset password</Button>
          </Popconfirm>
          <Button type="primary" onClick={() => navigate(`/employees/${id}/edit`)}>
            Edit profile
          </Button>
        </Space>
      </div>
      <Tabs
        style={{ marginTop: 8 }}
        destroyInactiveTabPane
        items={[
          { key: "overview", label: "Overview", children: overview },
          { key: "attendance", label: "Attendance", children: <EmployeeCalendarTab fixedEmployeeId={id} /> },
          { key: "leave", label: "Leave", children: leaveTab },
        ]}
      />
    </PageTransition>
  );
};

export default EmployeeProfile;
